// First-sign-in workspace readiness gate.
//
// A session/user can arrive before the SDK has populated its active workspace
// list. Both a `/w/<org>` deep link and an ordinary signed-in `/` boot must
// wait for that short resolution window before loading module config and the
// dashboard layout; otherwise the shell reveals defaults and visibly reshuffles
// when auth's late-claim reconciliation lands.

import { untrackedState } from './untrackedState.js'

const workspaceId = (root) => String(root?.workspace?.id || root?.workspace?._id || '')

// A warm snapshot deliberately seeds a LIST-shaped workspace so the shell can
// paint identity chrome before the live /core/boot response arrives. It is
// safe for chrome only: module config, installed apps and dashboard prefs are
// all workspace-scoped content and must wait for the live FULL record. A
// simple `workspace.id` check accepted that cache row and let the System
// workspace's module/layout load before the real workspace was known.
export const hasAuthoritativeWorkspace = (root, expectedWorkspaceId = null) => {
  const id = workspaceId(root)
  if (!id || root?._bootFromCache === true || root?.workspaceReady !== true) return false
  return !expectedWorkspaceId || id === String(expectedWorkspaceId)
}

const hasResolvableOrg = (root) =>
  !!(
    root?.org?.id ||
    root?.org?._id ||
    root?.jwtClaims?.workspace_org_id ||
    (Array.isArray(root?.orgs) && root.orgs.length)
  )

// Kept dependency-free so the race boundary can be unit-tested without a DOMQL
// browser runtime. The timeout is intentionally bounded: a signed-in user with
// no organization is on the create-org path, not a missing-workspace boot.
//
// WS-SPINNER-1 (tickets/opus.md, 2026-08-14): this poll gates `_dataLoading`
// — the GLOBAL shell MainLoader (see app.js `_isLoaderActive`) — for EVERY
// authenticated cold boot with a resolvable org, which is the common case for
// a bare (non-`/w/<org>`) deep link like `/admin` or `/chat`. The default
// 150 x 100ms ceiling (15s) was reused uniformly for two DIFFERENT
// guarantees that don't need the same bound:
//   1. EXACT-MATCH cases (`workspaceId` from a confirmed auth claim, or a
//      `/w/<org>` URL scope) — painting a DIFFERENT workspace before the
//      named one lands is visibly wrong, so this one genuinely needs a
//      generous ceiling to avoid the boot-reshuffle flicker class of bug.
//   2. The ORDINARY case (no URL scope, no confirmed claim id — just "a
//      signed-in user with a resolvable org") — there is no "wrong
//      workspace" this could paint first; it is simply waiting for ANY full
//      record to land. Reusing the 15s ceiling here meant a normal cold
//      boot to `/admin`/`/chat` that raced a merely-slow (not broken)
//      backend rode the SAME MainLoader up to its full worst case — measured
//      live as a 15+ second stuck spinner with nothing to show for the wait
//      once it gave up. `loadPrivateData`'s chains proceed regardless of
//      this function's return value either way (see its `_wsReady.then(...)`
//      call sites), so shortening the ordinary ceiling only speeds up how
//      soon they get to run — it changes no other behavior or the
//      exact-match guarantee above.
export const awaitInitialWorkspace = async (
  root,
  {
    urlScoped = false,
    workspaceId: expectedWorkspaceId = null,
    attempts = 150,
    // Ordinary (non-URL-scoped, non-exact-id) boots cap at a much shorter
    // ceiling — see the header comment above for why the full 15s bound is
    // safe to skip here. `Math.min(attempts, ordinaryAttempts)` so a caller
    // that explicitly passes a SMALLER `attempts` (tests, callers wanting an
    // even tighter bound) is never stretched back out.
    ordinaryAttempts = 60,
    intervalMs = 100
  } = {}
) => {
  const readRoot = () => untrackedState(root)
  const initialRoot = readRoot()
  if (hasAuthoritativeWorkspace(initialRoot, expectedWorkspaceId)) return true
  // An expected workspace id comes from a confirmed auth claim, or the URL
  // names a specific org/workspace scope. It must always wait for that exact
  // full record, even if an earlier warm snapshot or another tab's pointer
  // currently supplies a plausible workspace.
  const needsExactMatch = !!expectedWorkspaceId || urlScoped
  const shouldWait =
    needsExactMatch ||
    initialRoot?._bootFromCache === true ||
    (initialRoot?.signedIn && hasResolvableOrg(initialRoot))
  if (!shouldWait) return false
  const effectiveAttempts = needsExactMatch ? attempts : Math.min(attempts, ordinaryAttempts)
  for (
    let i = 0;
    i < effectiveAttempts && !hasAuthoritativeWorkspace(readRoot(), expectedWorkspaceId);
    i++
  ) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs))
  }
  return hasAuthoritativeWorkspace(readRoot(), expectedWorkspaceId)
}

// The async post-auth reconciliation lives in auth.js, while the global loader
// epoch belongs to loadPrivateData.js. Keep their handshake dependency-free and
// injectable so it can be exercised without mounting the DOMQL shell.
//
// Param names are deliberately NOT `beginDataLoad`/`endDataLoad` — those exact
// names are ALSO loadPrivateData.js's own top-level exports, and once this
// factory's returned `begin`/`end` closures became individually reachable
// (barrel-exported as `beginPostAuthWorkspaceReveal`/`endPostAuthWorkspaceReveal`,
// tickets/opus.md dropped-export sweep), esbuild's bundler renamed the
// PARAMETER to `beginDataLoad2`/`endDataLoad2` to disambiguate from the
// module-level export of the same name — a name frank's free-var classifier
// cannot resolve (unresolvable-scope-read). Same collision class as the
// `_isActive`/`_urlOrgSlug` renames elsewhere in this package; fixed the same
// way, by giving the closure-captured parameter a name that cannot collide
// bundle-wide.
export const createPostAuthWorkspaceRevealCoordinator = ({
  beginDataLoad: _beginDataLoad,
  endDataLoad: _endDataLoad
}) => {
  const pending = new WeakMap()

  const begin = (root, workspaceId) => {
    if (!root || !workspaceId) return null
    const id = String(workspaceId)
    const held = pending.get(root)
    if (held && held.workspaceId === id) return held
    const token = { workspaceId: id, epoch: _beginDataLoad(root) }
    pending.set(root, token)
    return token
  }

  const isActive = (root, workspaceId) => {
    const held = root && pending.get(root)
    return !!held && held.workspaceId === String(workspaceId || '')
  }

  const end = (root, token) => {
    if (!root || !token || pending.get(root) !== token) return false
    pending.delete(root)
    return _endDataLoad(root, token.epoch)
  }

  return { begin, isActive, end }
}
